import React, { createContext, useContext, useEffect, useState } from "react";
import { apiUrl, isAuthDisabled } from "../config/env";
import { getCurrentUser } from "../services/requests";
const tokenStorageKey = "c3-answer-auth-token";
const userStorageKey = "c3-answer-auth-user";

const localUser = {
  id: 0,
  email: "local-dev",
  username: "local-dev",
  name: "Local Developer",
  role: "owner",
  status: "active",
};

const AuthContext = createContext({
  isAuthenticated: false,
  isLoading: true,
  loading: true,
  user: null,
  login: async () => ({ success: false }),
  loginWithRedirect: async () => ({ success: false }),
  logout: () => {},
  getAccessTokenSilently: async () => null,
});

const isJwtToken = (value) =>
  typeof value === "string" && value.split(".").length === 3;

const readStoredToken = () => {
  const storedToken = window.localStorage.getItem(tokenStorageKey);

  if (!storedToken) {
    return null;
  }

  if (isJwtToken(storedToken)) {
    return storedToken;
  }

  window.localStorage.removeItem(tokenStorageKey);
  return null;
};

const readStoredUser = () => {
  const rawUser = window.localStorage.getItem(userStorageKey);

  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser);
  } catch (error) {
    window.localStorage.removeItem(userStorageKey);
    return null;
  }
};

export const AppAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() =>
    isAuthDisabled ? "local-dev-token" : readStoredToken()
  );
  const [user, setUser] = useState(() => (isAuthDisabled ? localUser : readStoredUser()));
  const [isLoading, setIsLoading] = useState(() =>
    isAuthDisabled ? false : Boolean(readStoredToken())
  );

  useEffect(() => {
    if (isAuthDisabled) {
      setToken("local-dev-token");
      setUser(localUser);
      return;
    }

    if (token && !isJwtToken(token)) {
      setToken(null);
      setUser(null);
      window.localStorage.removeItem(tokenStorageKey);
      window.localStorage.removeItem(userStorageKey);
      return;
    }

    if (token) {
      window.localStorage.setItem(tokenStorageKey, token);
    } else {
      window.localStorage.removeItem(tokenStorageKey);
    }

    if (user) {
      window.localStorage.setItem(userStorageKey, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(userStorageKey);
    }
  }, [token, user]);

  useEffect(() => {
    if (isAuthDisabled) {
      setIsLoading(false);
      return;
    }

    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    if (!isJwtToken(token)) {
      logout();
      setIsLoading(false);
      return;
    }

    let ignore = false;

    const loadCurrentUser = async () => {
      setIsLoading(true);
      try {
        const currentUser = await getCurrentUser(token);

        if (!ignore) {
          setUser(currentUser);
        }
      } catch (error) {
        if (!ignore) {
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    loadCurrentUser();

    return () => {
      ignore = true;
    };
  }, [token]);

  const login = async (username, password) => {
    if (isAuthDisabled) {
      return { success: true };
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${apiUrl}/v2/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Unable to login");
      }

      setToken(data.token);
      setUser(data.user || null);
      return { success: true };
    } catch (error) {
      setToken(null);
      setUser(null);
      return { success: false, message: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const getAccessTokenSilently = async () => {
    if (isAuthDisabled) {
      return "local-dev-token";
    }

    if (!token || !isJwtToken(token)) {
      logout();
      throw new Error("Not authenticated");
    }

    return token;
  };

  const value = {
    isAuthenticated: Boolean(token),
    isLoading,
    loading: isLoading,
    user,
    login,
    loginWithRedirect: login,
    logout,
    getAccessTokenSilently,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAppAuth = () => useContext(AuthContext);
